import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { toast } from 'sonner';
import type { ApiResponse } from '@/types/api';
import { getAuthToken, logout } from '@/stores/userStore';
import { cancelManager } from '.';
import { APP_CONFIG } from '@/config/env';
import { addMsgOnReloaded } from '@/utils/msgOnReload';

/**
 * 请求拦截器
 */
export const setupRequestInterceptor = (instance: AxiosInstance) => {
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = getAuthToken();
      // 如果存在 token,添加到请求头
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error: AxiosError) => {
      console.error('请求错误:', error);
      return Promise.reject(error);
    }
  );
};

/**
 * 响应拦截器
 */
export const setupResponseInterceptor = (instance: AxiosInstance) => {
  instance.interceptors.response.use(
    (response: AxiosResponse<ApiResponse>) => {
      cancelManager.removeRequest(response.config);
      if (APP_CONFIG.isDev) {
        console.log(`[cancelManager]移除已完成的请求：${response.config.headers?.['Request-Key']}`);
      }

      // 如果响应是二进制流（Blob/ArrayBuffer），直接返回，因为导出只会回传文件流
      if (response.data instanceof Blob || response.data instanceof ArrayBuffer) {
        return response;
      }

      const { code, msg } = response.data;
      if (code === 401 || code === 403) {
        // 清除用户信息（用于退出登录）
        logout();
        addMsgOnReloaded({ type: 'error', content: msg || '未授权,请重新登录' });
        window.location.hash = '/login';
        window.location.reload();
        return Promise.reject(new Error(msg || '未授权,请重新登录'));
      }
      // 业务成功
      if (code === 0) {
        return response;
      }
      // 业务错误
      toast.error(msg || '请求失败');
      return Promise.reject(new Error(msg || '请求失败'));
    },
    (error: AxiosError<ApiResponse>) => {
      if (error.config) {
        cancelManager.removeRequest(error.config);
        if (APP_CONFIG.isDev) {
          console.log(
            `[cancelManager]移除已${error.code === 'ERR_CANCELED' ? '取消' : '失败'}的请求：${error.config.headers?.['Request-Key']}`
          );
        }
      }
      // 处理 HTTP 错误
      if (error.response) {
        const { status, data } = error.response;

        switch (status) {
          case 401:
            toast.error('未授权,请重新登录');
            // 清除用户信息（用于退出登录）
            logout();
            break;
          case 403:
            toast.error('无权限访问');
            break;
          case 404:
            toast.error('请求的资源不存在');
            break;
          case 500:
          case 502:
          case 503:
          case 504:
            toast.error('服务器错误,请稍后重试');
            break;
          default:
            toast.error(data?.msg || '请求失败');
        }
      } else if (error.request) {
        // 请求已发出但没有收到响应
        if (error.code === 'ECONNABORTED') {
          toast.error('请求超时,请稍后重试');
        } else if (error.code !== 'ERR_CANCELED') {
          toast.error('网络连接失败,请检查网络设置');
        }
      } else if (error.code !== 'ERR_CANCELED') {
        // 其他错误
        toast.error(error.message || '请求失败');
      }
      if (error.code !== 'ERR_CANCELED') {
        console.error('响应错误:', error);
      }

      return Promise.reject(error);
    }
  );
};

/**
 * 设置所有拦截器
 */
export const setupInterceptors = (instance: AxiosInstance) => {
  setupRequestInterceptor(instance);
  setupResponseInterceptor(instance);
};
